import React, { Component, createRef, Fragment } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import Props from './SeriesTimePickerComponent';
import { DateTimeFormatters } from '@showclix-shared-intl';
import MenuDatePickerContainer from '../containers/MenuDatePickerContainer';
import Skeleton from 'shared/Skeleton';
import { eventInFilterResults } from '../../../helpers/series';
import { TranslationContext } from '../../../../checkout/config/Translations';
import Translation from '../../../../shared/Translation';
import Button from 'shared/Button';

const { weekDayMonthDayYearShort } = DateTimeFormatters;

const MAX_EVENT_BUTTONS = 6;
const BUTTONS_GAP = 5;

export default class SeriesTimePicker extends Component<Props> {
  constructor(props) {
    super(props);

    this.eventTimesGroupRef = createRef();

    this.state = {
      selectedChildEventId: null,
      eventRanges: [],
      selectedEventRange: false,
      selectedChildEventTime: null,
      isMinimized: false
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selectedDay != this.props.selectedDay) {
      this.setState({
        selectedChildEventId: null
      });
    }

    if (
      prevProps.eventTimes.length !== this.props.eventTimes.length &&
      this.props.eventTimes.length > MAX_EVENT_BUTTONS
    ) {
      this.setState({ eventRanges: this.getEventRanges() });
    }
  }


  // click  "change date" button when times are shown
  onChangeDate = () => {
    this.props?.setIsDateChanging(true);
    this.setState({
      isMinimized: false
    });
  }

  getDateString = (eventTime) => {
    if (!this.props.selectedDayDate || !eventTime) return undefined;

    const dateString = weekDayMonthDayYearShort(this.props.locale, this.props.selectedDayDate);

    if (eventTime) return `${dateString}, ${eventTime}`;

    return dateString;
  }

  render() {
    const selectedEventTime = this.getSelectedEventTime();

    const eventTime = this.getEventTime(selectedEventTime);

    const dateString = this.getDateString(eventTime);

    return (
      <div className="series__time-picker">
        <div className="picker__header">
            <i
              className={eventTime ? 'icon-calendar' : 'icon-clock'}
              aria-label={eventTime ? 'calendar' : 'clock'}
            />

            <h4 className="picker__header-title">
              {dateString || <Translation>{'checkout__select_time'}</Translation>}
            </h4>
            {(eventTime && !this.props.isChangingDate && this.props.setIsDateChanging) && (
              <Button
                className="button--clear header__action-button"
                onClick={this.onChangeDate}
                type="button"
              >
                <Translation>{'checkout__change_date'}</Translation>
              </Button>
            )}
        </div>

        {this.props.selectedDateDescription && (
          <div
            className="tickets__selected-day"
            dangerouslySetInnerHTML={{ __html: this.props.selectedDateDescription }}
          />
        )}
        {this.getEventTimes()}
      </div>
    );
  }

  getEventRanges = () => {
    let eventRanges = [];
    let events = [...this.props.eventTimes];

    while (events.length > 0) {
      let eventList = events.splice(0, MAX_EVENT_BUTTONS);
      for (let i = 0; i < eventList.length; i++) {
        eventList[i].singleTime = eventList[i].time;
      }
      eventRanges.push(eventList);
    }

    return eventRanges;
  };

  changeEventRange = e => {
    if (e.target.value !== '') {
      this.setState({ selectedEventRange: e.target.value });
    } else {
      this.setState({ selectedEventRange: false });
    }
  };

  getEventTime = event => {
    return event ? this.props.getEventTimeFormatted(event.time, event.endTime) : null;
  };

  getSelectedEventId = () => {
    return this.state.selectedChildEventId ? this.state.selectedChildEventId : this.props.selectedChildEvent;
  };

  getSelectedEventTime = () => {
    const id = this.getSelectedEventId();
    for (let i = 0; i < this.props.eventTimes.length; i++) {
      if (this.props.eventTimes[i].id == id) {
        return this.props.eventTimes[i];
      }
    }

    return null;
  };

  onShowAll = () => {
    this.setState({ isMinimized: false });
  }

  isEventButtonsOverflowing = () => {
    if (!this.eventTimesGroupRef.current) {
      return false;
    }

    const { offsetWidth: containerWidth, children } = this.eventTimesGroupRef.current;

    let childrenWidth = 0;

    for (let i = 0, length = children.length; i < length; i++) {
      childrenWidth += children[i].scrollWidth;
      const totalWidth = childrenWidth + i * BUTTONS_GAP;

      if (totalWidth > containerWidth) {
        return true;
      }
    }

    return false;
  }

  getButtons = times => {
    const isOverflowing = this.isEventButtonsOverflowing();

    return (
      <TranslationContext.Consumer>
        {translation => (
            <div className={clsx("event__times", {
              "event__times--minimized": this.state.isMinimized
            })}>
                <div ref={this.eventTimesGroupRef} className="event__times__group">
                  {times.map((eventTime, i) => {
                    // For autoFocus: first 'on sale' time
                    // React will crash if it cannot find ID / on sale time
                    if (times.find(x => x.eventStatus === 'on_sale')) {
                      var firstAvailableItem = times.find(x => x.eventStatus === 'on_sale').id;
                    } else {
                      // No avaliable times
                      // eslint-disable-next-line no-redeclare
                      var firstAvailableItem = null;
                    }
                    if (eventTime.eventStatus === 'sold_out' && this.props.showSoldOut === false) {
                      return null;
                    }

                    let checkFilterResults = this.props.hasActiveInventoryFilter && this.props.inventoryFilterResults;
                    return (
                      <button
                        className={`button event-time-button event__times--fade
                                          ${
                                            checkFilterResults &&
                                            !eventInFilterResults(+eventTime.id, this.props.inventoryFilterResults)
                                              ? 'filtered'
                                              : ''
                                          }
                                          ${this.props.selectedChildEvent === Number(eventTime.id) ? 'selected' : ''}
                                          ${
                                            eventTime.eventStatus === 'sold_out' || eventTime.eventStatus === 'post_sale'
                                              ? 'disabled'
                                              : ''
                                          }`}
                        value={eventTime.id}
                        key={i}
                        onClick={this.onEventTimeSelect}
                        autoFocus={this.props.autoFocus && eventTime.id === firstAvailableItem}
                      >
                        {this.getEventTime(eventTime)}
                      </button>
                    );
                  })}
                </div>
                {this.getStatusMessage(translation)}
                {/* {isOverflowing && (
                    <div className="expand-action">
                        <Button
                          className="button--clear"
                          onClick={this.onShowAll}
                        >
                          Show All Times
                        </Button>
                    </div>
                )} */}
            </div>
        )}
      </TranslationContext.Consumer>
    );
  };

  getEventTimes = () => {
    if (!Array.isArray(this.props.eventTimes) || (this.props.eventTimes.length === 0 && !this.props.loading)) {
      if (this.props.networkError) {
        return (
          <p>
            <Translation>{'checkout__network_error'}</Translation>
          </p>
        );
      };
      return (
        <p>
          <Translation>{'checkout__events_on_day_not_available'}</Translation>
        </p>
      );
    }

    if (this.props.calendarType === 'calendar-button') {
      if (this.props.loading) {
        return (
          <div className="event__times">
            <div className="event__times__group">
              <Skeleton
                className="event-times-group__buttons--loading"
                height="36px"
                width="90px"
                borderRadius="50px"
                display="inline-block"
              />
              <Skeleton
                className="event-times-group__buttons--loading"
                height="36px"
                width="90px"
                borderRadius="50px"
                display="inline-block"
              />
              <Skeleton
                className="event-times-group__buttons--loading"
                height="36px"
                width="90px"
                borderRadius="50px"
                display="inline-block"
              />
            </div>
          </div>
        );
      } else {
        if (this.state.eventRanges.length === 0 || this.props.showButtonTimeRanges === false) {
          return this.getButtons(this.props.eventTimes);
        } else {
          return (
            <TranslationContext.Consumer>
              {t => (
                <div>
                  <div className="input">
                    <select
                      className="input__control input__select event__times--fade"
                      name="event-ranges"
                      onChange={this.changeEventRange}
                    >
                      <option value="">{t.t('checkout__select_time_range')}</option>
                      {this.state.eventRanges.map((range, i) => {
                        const label = this.props.getEventTimeFormatted(
                          range[0].singleTime,
                          range[range.length - 1].singleTime
                        );
                        return (
                          <option value={i} key={label}>
                            {label}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  {this.state.selectedEventRange !== false &&
                    this.getButtons(this.state.eventRanges[this.state.selectedEventRange])}
                </div>
              )}
            </TranslationContext.Consumer>
          );
        }
      }
    } else {
      if (this.props.loading) {
        return (
          <div className="card-group event__times--fade">
            <div className="card">
              <Skeleton height="13px" width="166px" />
            </div>
            <div className="card">
              <Skeleton height="10px" width="70px" />
            </div>
            <div className="card">
              <Skeleton height="10px" width="100px" />
            </div>
          </div>
        );
      } else {
        return (
          <MenuDatePickerContainer
            selectedDay={this.props.selectedDayDate}
            skipTimeLoad={true}
            setIsDateChanging={this.props.setIsDateChanging}
          />
        );
      }
    }
  };

  //get message to display (if any) when user selects an event
  getStatusMessage = translation => {
    let eventId = this.state.selectedChildEventId ? this.state.selectedChildEventId : this.props.selectedChildEvent;
    let selectedChildEventTime = this.state.selectedCildEventTime || this.getSelectedEventTime();
    if (eventId) {
      let inventory = false;
      let eventStatus = null;
      let formattedEventTime = this.getEventTime(selectedChildEventTime);
      for (let i = 0; i < this.props.eventTimes.length; i++) {
        if (this.props.eventTimes[i].id == eventId) {
          inventory = this.props.eventTimes[i].inventory;
          eventStatus = this.props.eventTimes[i].eventStatus;
          break;
        }
      }

      if (eventStatus === 'sold_out' || eventStatus === 'pre_sale' || eventStatus === 'post_sale') {
        let messageMap = {
          sold_out: this.props.getSoldOutMessage(),
          pre_sale: 'Presale only',
          post_sale: 'Online sales closed'
        };
        return (
          <div className="series__time-picker__message">
            <span className="sold-out__message">
              <span className="sold-out__message__time">{formattedEventTime}:</span> {messageMap[eventStatus]}
            </span>
          </div>
        );
      }

      let lowInvMessage = this.props.getLowInventoryMessage(inventory, translation);
      if (lowInvMessage) {
        return (
          <div className="series__time-picker__message">
            <span className="low-inventory__message">
              {formattedEventTime}: {lowInvMessage}
            </span>
          </div>
        );
      }
    }
    return null;
  };

  getScrollToPosition = (element) => {
    const { offsetLeft, scrollWidth } = element;
    return (
      offsetLeft - 50
    );
  }

  onEventTimeSelect = e => {
    let id = +e.currentTarget.value;
    let time = null;
    let targetEventTime = null;
    let seriesChildAdditionalInfo = null;
    this.props.eventTimes.map(eventTime => {
      if (eventTime.id == id) {
        targetEventTime = eventTime;
      }
    });

    this.setState({
      selectedChildEventId: id,
      selectedChildEventTime: time,
      isMinimized: true
    });

    if (targetEventTime) {
      this.setState({ selectedChildEventTime: targetEventTime });
      if (targetEventTime.eventStatus === 'post_sale' || targetEventTime.eventStatus === 'sold_out') {
        this.props.onEventTimeChange(null);
        return false;
      }
      seriesChildAdditionalInfo = targetEventTime.childEventAdditionalInfo;
      this.setState({ seriesChildAdditionalInfo: seriesChildAdditionalInfo });
      this.props.onEventTimeChange(id, seriesChildAdditionalInfo);
    }

    setTimeout(() => {
      this.eventTimesGroupRef.current?.scrollTo(
        {
          left: this.getScrollToPosition(e.target),
          top: 20,
          behavior: 'smooth'
        });
    });

  };
}

SeriesTimePicker.propTypes = {
  locale: PropTypes.string.isRequired,
  onEventTimeChange: PropTypes.func.isRequired,
  showSoldOut: PropTypes.bool.isRequired,
  soldOutMessage: PropTypes.string,
  showButtonTimeRanges: PropTypes.bool,
  selectedDateDescription: PropTypes.string,
  networkError: PropTypes.bool
};

SeriesTimePicker.defaultProps = {
  showButtonTimeRanges: false
};
